import { Pipe, PipeTransform } from '@angular/core';

/**
 * Duration Format Pipe
 * Converts a duration (in seconds, milliseconds, or HH:MM:SS string) to a human-readable format
 * 
 * Examples:
 * - 179 hours → "7d 11h"
 * - 3 hours → "3h 15m"
 * - 45 minutes → "45m 30s"
 */
@Pipe({
    name: 'durationFormat',
  standalone: false
})
export class DurationFormatPipe implements PipeTransform {
    /**
     * Transform a duration to human-readable format
     * @param value - Duration as seconds, milliseconds, or HH:MM:SS string
     * @param inputUnit - 'seconds' | 'milliseconds' | 'hhmmss' (default: 'seconds')
     * @returns Formatted string like "7d 11h", "3h 15m", or "45m 30s"
     */
    transform(value: number | string | null | undefined, inputUnit: 'seconds' | 'milliseconds' | 'hhmmss' = 'seconds'): string {
        if (value === null || value === undefined) {
            return '--';
        }

        let totalSeconds: number;

        if (typeof value === 'string') {
            // Handle HH:MM:SS format
            if (inputUnit === 'hhmmss' || value.includes(':')) {
                totalSeconds = this.parseHHMMSS(value);
            } else {
                totalSeconds = parseFloat(value);
            }
        } else {
            totalSeconds = inputUnit === 'milliseconds' ? Math.floor(value / 1000) : value;
        }

        if (isNaN(totalSeconds) || totalSeconds < 0) {
            return '--';
        }

        return this.formatDuration(totalSeconds);
    }

    /**
     * Parse HH:MM:SS string to total seconds
     */
    private parseHHMMSS(value: string): number {
        const parts = value.split(':').map(p => parseInt(p, 10));
        
        if (parts.length === 3) {
            // HH:MM:SS
            const [hours, minutes, seconds] = parts;
            return (hours * 3600) + (minutes * 60) + seconds;
        } else if (parts.length === 2) {
            // MM:SS
            const [minutes, seconds] = parts;
            return (minutes * 60) + seconds;
        }
        
        return parseInt(value, 10) || 0;
    }

    /**
     * Format seconds to human-readable duration
     */
    private formatDuration(totalSeconds: number): string {
        const days = Math.floor(totalSeconds / 86400);
        const hours = Math.floor((totalSeconds % 86400) / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = Math.floor(totalSeconds % 60);

        if (days > 0) {
            return `${days}d ${hours}h`;
        } else if (hours > 0) {
            return `${hours}h ${minutes}m`;
        } else if (minutes > 0) {
            return `${minutes}m ${seconds}s`;
        } else {
            return `${seconds}s`;
        }
    }
}
